import React, { useState, useMemo, useEffect } from 'react';
import { useDetailsModalSave } from '../../hooks/useDetailsModalSave';
import { AssignmentApi } from '../../services/api/AssignmentApi';
import '../details/DetailsSection.css';

export default function AssignmentDetails({
  assignment,
  onClose,
  onUpdate,
  onSaved,
  onMarkComplete,
  onEdit,
  onDelete,
  endeavor,
  navigateToEndeavor,
  navigateToEvent
}) {
  const [editedData, setEditedData] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [localCompleted, setLocalCompleted] = useState(assignment.completed);
  const hasChanges = Object.keys(editedData).length > 0;

  // Sync local completed state when assignment prop changes
  useEffect(() => {
    setLocalCompleted(assignment.completed);
  }, [assignment.completed]);

  // Use shared modal save hook with optimistic update + rollback
  const { handleSave: performSave, saving, error } = useDetailsModalSave({
    entity: assignment,
    entityType: 'assignment',
    buildPatch: (editedData, assignment) => {
      console.log('[AssignmentDetails] buildPatch called:', {
        editedData,
        assignmentCompleted: assignment.completed,
        editedCompleted: editedData.completed
      });
      
      // Merge edited fields with full assignment object
      const merged = { ...assignment, ...editedData };
      // Ensure assignmentId is always present for backend validation
      merged.assignmentId = assignment.assignmentId || assignment.id;
      
      console.log('[AssignmentDetails] buildPatch result:', {
        mergedCompleted: merged.completed,
        assignmentId: merged.assignmentId || merged.id
      });
      
      // If dueDatetime was edited, convert from local datetime-local to ISO
      if (editedData.dueDatetime) {
        if (typeof editedData.dueDatetime === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(editedData.dueDatetime)) {
          const date = new Date(editedData.dueDatetime);
          const offset = date.getTimezoneOffset();
          const adjustedDate = new Date(date.getTime() - (offset * 60000));
          merged.dueDatetime = adjustedDate.toISOString();
        }
      }
      
      return merged;
    },
    apiUpdate: async (id, payload) => {
      return await AssignmentApi.updateAssignment(id, payload);
    },
    applyLocalUpdate: onUpdate,
    onSaved,
    onSuccess: () => {
      setEditingField(null);
      setEditedData({});
    }
  });

  const loading = saving;

  // Calculate derived properties
  const isOverdue = useMemo(() => {
    if (!assignment?.dueDatetime) return false;
    const now = new Date();
    const due = new Date(assignment.dueDatetime);
    return due < now && !localCompleted;
  }, [assignment?.dueDatetime, localCompleted]);

  const assignmentInfo = useMemo(() => {
    if (!assignment) return { createdAt: 'Unknown', updatedAt: 'Unknown', status: 'Pending' };
    
    const createdAt = assignment.createdAt
      ? new Date(assignment.createdAt).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        })
      : 'Unknown';
    
    const updatedAt = assignment.updatedAt
      ? new Date(assignment.updatedAt).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        })
      : 'Unknown';

    let status = 'Pending';
    if (localCompleted) status = 'Completed';
    else if (isOverdue) status = 'Overdue';

    return { createdAt, updatedAt, status };
  }, [assignment, isOverdue, localCompleted]);

  // Clear edited data when assignment prop changes (after save from parent)
  useEffect(() => {
    setEditedData({});
    setEditingField(null);
  }, [assignment]);

  if (!assignment) return null;

  const isFromSyllabus =
    Boolean(assignment.isFromSyllabus) ||
    /SYLLABUS/i.test(String(assignment.source || ''));

  const hasWeight = Number.isFinite(Number(assignment.weightPercent));
  const weightText = hasWeight ? `${Number(assignment.weightPercent)}%` : null;
  const isGroup = Boolean(assignment.groupWork);

  // Normalize priority so we never call toLowerCase() on a non-string
  const PRIORITY_NUM_TO_KEY = {
    0: 'none',
    1: 'low',
    2: 'medium',
    3: 'high',
    4: 'critical'
  };
  const rawPriority = assignment.priority;
  let priorityKey = 'medium';
  if (typeof rawPriority === 'number' && Number.isFinite(rawPriority)) {
    priorityKey = PRIORITY_NUM_TO_KEY[rawPriority] || 'medium';
  } else if (typeof rawPriority === 'string') {
    priorityKey = rawPriority.trim().toLowerCase() || 'medium';
  }
  const priorityLabel = priorityKey.charAt(0).toUpperCase() + priorityKey.slice(1); // eslint-disable-line no-unused-vars

  // Helper to convert date to local datetime-local format (not UTC)
  const toLocalDatetimeString = (dateString) => {
    if (!dateString) return '';
    
    // If it's already in datetime-local format (YYYY-MM-DDTHH:mm), return as-is
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Otherwise it's an ISO string, convert to local datetime format
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const startEditingField = (field, currentValue) => {
    if (editingField) return; // Don't allow editing multiple fields at once
    setEditingField(field);
    if (field === 'dueDatetime') {
      setEditedData({ ...editedData, [field]: toLocalDatetimeString(currentValue) });
    } else {
      setEditedData({ ...editedData, [field]: currentValue ?? '' });
    }
  };

  const handleSave = async () => {
    if (!hasChanges) {
      setEditingField(null);
      return;
    }
    await performSave(editedData);
  };

  const updateField = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const saveChanges = handleSave; // Alias for compatibility

  const handleToggleCompleted = async () => {
    const newCompletedStatus = !localCompleted;
    
    console.log('[AssignmentDetails] Toggle completion clicked:', {
      currentLocalCompleted: localCompleted,
      newCompletedStatus,
      assignmentPropCompleted: assignment.completed,
      assignmentId: assignment.assignmentId || assignment.id
    });
    
    // Optimistically update local state immediately
    setLocalCompleted(newCompletedStatus);
    
    try {
      // Use the same save flow as other fields for consistency
      await performSave({ completed: newCompletedStatus });
      console.log('[AssignmentDetails] Toggle completion save completed successfully');
    } catch (err) {
      console.error('[AssignmentDetails] Failed to toggle completion:', err);
      // Rollback on failure
      setLocalCompleted(!newCompletedStatus);
    }
  };

  return (
    <div className="assignment-combined" data-surface="assignment-details">
      {error && (
        <div style={{
          background: 'var(--intent-danger-bg)',
          color: 'var(--intent-danger-text)',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid var(--intent-danger-border)'
        }}>
          {error}
        </div>
      )}

      <div className="edit-helper-tip">
        Tip: Click any highlighted field to edit.
      </div>

      <div className="assignment-top-row">
        <div className="assignment-icon-large">
          {assignment.assignmentType === 'exam' ? '📝' :
           assignment.assignmentType === 'project' ? '💼' :
           assignment.assignmentType === 'quiz' ? '❓' :
           assignment.assignmentType === 'reading' ? '📖' : '📋'}
        </div>
        <div className="assignment-top-meta">
          {editingField === 'title' ? (
            <input
              type="text"
              className="inline-edit-input title-input"
              value={editedData.title ?? assignment.title ?? ''}
              onChange={(e) => updateField('title', e.target.value)}
              autoFocus
              style={{ 
                fontSize: '1.5rem', 
                fontWeight: '600',
                width: '100%',
                padding: '0.25rem 0.5rem',
                border: '2px solid var(--semantic-primary)',
                borderRadius: '4px'
              }}
            />
          ) : (
            <h1 
              className="assignment-title editable-field" 
              onClick={() => startEditingField('title', assignment.title)}
            >
              {editedData.title ?? assignment.title}
            </h1>
          )}

          <div className="assignment-links-row">
            {isFromSyllabus && (
              <span className="assignment-link-btn" title="Extracted from syllabus">🤖 From Syllabus</span>
            )}
            {hasWeight && (
              <span className="assignment-link-btn" title="Weight">🏷️ {weightText}</span>
            )}
            {isGroup && (
              <span className="assignment-link-btn" title="Group work">👥 Group</span>
            )}
            {endeavor && navigateToEndeavor && (
              <button className="assignment-link-btn" onClick={navigateToEndeavor}>
                🎓 {endeavor.name}
              </button>
            )}
            {assignment.eventId && navigateToEvent && (
              <button className="assignment-link-btn" onClick={navigateToEvent}>
                📅 Related Event
              </button>
            )}
          </div>
        </div>
        <div className="header-actions combined-actions">
          <button
            className={`btn ${localCompleted ? 'btn-warning' : 'btn-success'}`}
            onClick={handleToggleCompleted}
            disabled={loading}
          >
            {loading ? '...' : (localCompleted ? '↻ Mark Incomplete' : '✓ Mark Complete')}
          </button>
          {hasChanges && (
            <button
              className="btn btn-primary"
              onClick={saveChanges}
              disabled={loading}
              style={{ marginLeft: '0.5rem' }}
            >
              {loading ? 'Saving...' : '💾 Save'}
            </button>
          )}
        </div>
      </div>

      {editingField === 'description' ? (
        <textarea
          className="inline-edit-input description-input"
          value={editedData.description ?? assignment.description ?? ''}
          onChange={(e) => updateField('description', e.target.value)}
          autoFocus
          placeholder="Add description..."
          rows={4}
          style={{
            width: '100%',
            padding: '0.75rem',
            border: '2px solid var(--semantic-primary)',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.95rem',
            fontFamily: 'inherit'
          }}
        />
      ) : (
        <div 
          className={`assignment-description-block editable-field ${!assignment.description ? 'empty-field' : ''}`}
          onClick={() => startEditingField('description', assignment.description || '')}
          style={!assignment.description ? { fontStyle: 'italic', opacity: 0.6, padding: '0.75rem', cursor: 'pointer' } : { cursor: 'pointer' }}
        >
          {editedData.description ?? assignment.description ?? 'Click to add description...'}
        </div>
      )}

      <div className="assignment-details-grid">
        <div className="detail-item">
          <label>Due Date</label>
          {editingField === 'dueDatetime' ? (
            <input
              type="datetime-local"
              className="inline-edit-input"
              value={toLocalDatetimeString(editedData.dueDatetime ?? assignment.dueDatetime)}
              onChange={(e) => {
                // Store the raw datetime-local string value (don't convert yet)
                updateField('dueDatetime', e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              style={{
                padding: '0.25rem 0.5rem',
                border: '2px solid var(--semantic-primary)',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            />
          ) : (
            <div 
              className="editable-inner"
              onClick={() => startEditingField('dueDatetime', assignment.dueDatetime)}
            >
              <span className={isOverdue ? 'overdue-text' : ''}>
                {(editedData.dueDatetime ?? assignment.dueDatetime)
                  ? new Date(editedData.dueDatetime ?? assignment.dueDatetime).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit', hour12: true
                    })
                  : 'Not set'}
              </span>
            </div>
          )}
        </div>
        <div className="detail-item">
          <label>Priority</label>
          {editingField === 'priority' ? (
            <select
              className="inline-edit-input"
              value={editedData.priority ?? priorityKey}
              onChange={(e) => updateField('priority', e.target.value)}
              autoFocus
              style={{
                padding: '0.25rem 0.5rem',
                border: '2px solid var(--semantic-primary)',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            >
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          ) : (
            <div 
              className="editable-inner"
              onClick={() => startEditingField('priority', priorityKey)}
            >
              <span className={`priority-badge priority-${editedData.priority ?? priorityKey}`}>
                {(editedData.priority ?? priorityKey).charAt(0).toUpperCase() + (editedData.priority ?? priorityKey).slice(1)}
              </span>
            </div>
          )}
        </div>
        <div className="detail-item">
          <label>Status</label>
          <p><span className={`status-badge ${assignment.completed ? 'completed' : (isOverdue ? 'overdue' : 'pending')}`}>
            {assignmentInfo.status}
          </span></p>
        </div>
        {assignment.points != null && (
          <div className="detail-item">
            <label>Points</label>
            {editingField === 'points' ? (
              <input
                type="number"
                className="inline-edit-input"
                value={editedData.points ?? assignment.points ?? ''}
                onChange={(e) => updateField('points', e.target.value)}
                autoFocus
                placeholder="Points"
                style={{
                  padding: '0.25rem 0.5rem',
                  border: '2px solid var(--semantic-primary)',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  width: '80px'
                }}
              />
            ) : (
              <div 
                className="editable-inner"
                onClick={() => startEditingField('points', assignment.points)}
              >
                {editedData.points ?? assignment.points}
              </div>
            )}
          </div>
        )}
        {hasWeight && (
          <div className="detail-item">
            <label>Weight</label>
            {editingField === 'weightPercent' ? (
              <input
                type="number"
                className="inline-edit-input"
                value={editedData.weightPercent ?? assignment.weightPercent ?? ''}
                onChange={(e) => updateField('weightPercent', e.target.value)}
                autoFocus
                placeholder="Weight %"
                style={{
                  padding: '0.25rem 0.5rem',
                  border: '2px solid var(--semantic-primary)',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  width: '80px'
                }}
              />
            ) : (
              <div 
                className="editable-inner"
                onClick={() => startEditingField('weightPercent', assignment.weightPercent)}
              >
                {editedData.weightPercent ?? weightText}
              </div>
            )}
          </div>
        )}
        {isGroup && (
          <div className="detail-item"><label>Group Work</label><p>Yes</p></div>
        )}
        <div className="detail-item"><label>Created</label><p>{assignmentInfo.createdAt}</p></div>
        <div className="detail-item"><label>Updated</label><p>{assignmentInfo.updatedAt}</p></div>

        {assignment.dueInstructions && (
          <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
            <label>Due Instructions</label>
            {editingField === 'dueInstructions' ? (
              <textarea
                className="inline-edit-input"
                value={editedData.dueInstructions ?? assignment.dueInstructions ?? ''}
                onChange={(e) => updateField('dueInstructions', e.target.value)}
                autoFocus
                placeholder="Add instructions..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '2px solid var(--semantic-primary)',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  fontFamily: 'inherit'
                }}
              />
            ) : (
              <div 
                className="editable-inner"
                onClick={() => startEditingField('dueInstructions', assignment.dueInstructions)}
              >
                {editedData.dueInstructions ?? assignment.dueInstructions}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
